import { useEffect, useState } from 'react';
import { extractFrameProfileSpriteMarkup } from '../assets/frameProfileSprite';
import type { FrameArtworkProfile } from '../types/doorConfiguration';
import { applySvgColourPalette } from './frameColourPaletteCss';

/**
 * Fetch a profile SVG and return frame-only markup for same-document
 * nine-slice `<use>` cropping. A supplied palette overrides the authored
 * source palette; null preserves the canonical artwork exactly.
 * Returns null until loaded (Jest fetch fails → calculated frame only).
 */
export function useSvgAssetWithPalette(
  publicPath: string,
  contentVersion: string,
  profile: FrameArtworkProfile,
  paletteCss: string | null
): string | null {
  const [markup, setMarkup] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMarkup(null);

    if (typeof fetch !== 'function') {
      return undefined;
    }

    const versionedPath = `${publicPath}?v=${encodeURIComponent(contentVersion)}`;
    fetch(versionedPath, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load ${versionedPath}`);
        return response.text();
      })
      .then((text) => {
        if (cancelled) return;
        const colouredMarkup = paletteCss
          ? applySvgColourPalette(text, paletteCss)
          : text;
        setMarkup(
          extractFrameProfileSpriteMarkup(
            colouredMarkup,
            profile
          )
        );
      })
      .catch(() => {
        if (!cancelled) setMarkup(null);
      });

    return () => {
      cancelled = true;
    };
  }, [publicPath, contentVersion, profile, paletteCss]);

  return markup;
}
